import { useLocation, useNavigate } from 'react-router-dom';
import { useConfigureLineContext } from '../../../../context/configure-line-context/ConfigureLineContext';
import {
    ButtonPrimary,
    Callout,
    Divider,
    IconChevronLeftRegular,
    IconLockClosedRegular,
    IconWarningRegular,
    NavigationBar,
    Row,
    RowList,
    skinVars,
    Text1,
    Text2,
    Title2,
} from '@telefonica/mistica';
import { t } from 'i18next';
import { getRoleLine } from '../../../../utils/dataTransformation/productConsumption';
import './index.scss';
import { isWebviewAndroid, isWebviewIOS } from '../../../../utils/navigation/controlDevices';
import { formatMobileNumber } from '../../../../utils/dataTransformation/unitsConversion';
import { getCustomerData } from '../../../../services/CustomerService';
import { useQuery } from '@tanstack/react-query';
import { addMultiSim, duplicateSIM, getSIMSInfo } from '../../../../services/ConfigureLineService';
import { AxiosError } from 'axios';
import BreadCrumbsWrapper from '../../../../components/BreadcrumbsWrapper';

const DuplicateSimRequest = () => {
    const { simTypeSelected, lineActive, setDuplicateSimSuccess, setConfigureLineError, isConvergent } =
        useConfigureLineContext();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { data: customerData } = useQuery({ queryKey: ['customer-data'], queryFn: getCustomerData });
    const { data: infoSim } = useQuery({
        queryKey: ['sims-info', lineActive],
        queryFn: () => getSIMSInfo(lineActive),
        enabled: !!lineActive,
    });

    const consumptions = JSON.stringify(window.localStorage.getItem('storages'));
    const isMultiSim = pathname.includes('multiSim');

    const navigationBackHandler = () => navigate(-1);

    const simRequest = async () => {
        try {
            if (isMultiSim) {
                if (simTypeSelected === 'PHYSICAL_SIM') await addMultiSim(infoSim?.productId!, 'PHYSICAL_SIM');
                else await addMultiSim(infoSim?.productId!, 'E_SIM');
            } else {
                await duplicateSIM(infoSim?.productId!);
            }

            localStorage.setItem('timestampDuplicate', JSON.stringify({ time: `${new Date()}` }));
            setDuplicateSimSuccess(true);
        } catch (error) {
            setDuplicateSimSuccess(false);
            setConfigureLineError(error as AxiosError);
        } finally {
            if (!isMultiSim) navigate(`/configure-line/sim/feedbackDuplicateSIM`);
            else navigate(`/configure-line/multiSim/feedbackMultiSim`);
        }
    };

    return (
        <div className='duplicate-sim-request'>
            <div className='duplicate-sim-request__header'>
                {!(isWebviewAndroid || isWebviewIOS) && (
                    <div>
                        <div className='duplicate-sim-request__header-navigationBar'>
                            <NavigationBar
                                onBack={navigationBackHandler}
                                title={t(isMultiSim ? 'multiSimRequestHeader' : 'duplicateRequest')}
                            />
                        </div>
                        <div className='duplicate-sim-request__header-return' onClick={navigationBackHandler}>
                            <div className='duplicate-sim-request__header-return-icon'>
                                <IconChevronLeftRegular color={skinVars.colors.textPrimary} size={20} />
                            </div>
                            <Text2 medium color={skinVars.colors.textPrimary}>
                                {t('returnResponsive')}
                            </Text2>
                        </div>
                    </div>
                )}

                <div className='blocked-line__breadcrumbs'>
                    <BreadCrumbsWrapper
                        title={t(isMultiSim ? 'multiSimRequestHeader' : 'duplicateRequest')}
                        breadcrumbs={
                            !isConvergent
                                ? [
                                      { title: t('breadcrumbsHome'), url: '/home' },
                                      {
                                          title: t('lineManagement'),
                                          url: `/configure-line`,
                                      },
                                  ]
                                : [
                                      { title: t('breadcrumbsHome'), url: '/home' },
                                      {
                                          title: getRoleLine(JSON.parse(consumptions), infoSim?.productPsId!),
                                          url: `/mobile?productPsId=${infoSim?.productPsId}`,
                                      },
                                      {
                                          title: t('lineManagement'),
                                          url: `/configure-line`,
                                      },
                                  ]
                        }
                    />
                </div>
            </div>

            <Divider />

            <div className='duplicate-sim-request__body'>
                <div className='duplicate-sim-request__body-title'>
                    <Title2>{t('requestSummary')}</Title2>
                </div>

                {isMultiSim && (
                    <Text1 regular color={skinVars.colors.neutralMedium}>
                        {t('multiSim').toUpperCase()}
                    </Text1>
                )}

                <RowList>
                    {simTypeSelected === 'PHYSICAL_SIM' && (
                        <Row title={t('deliveryAddress')} subtitle={customerData?.data.address} />
                    )}
                    <Row title={t('email')} subtitle={customerData?.data.email.value} />
                    <Row title={t('mobileLine')} subtitle={formatMobileNumber(infoSim?.number)} />
                    {isMultiSim && <Row title={t('serviceCost')} subtitle='5€ / mes' />}
                </RowList>

                <Callout
                    asset={<IconWarningRegular color={skinVars.colors.brand} size={24} />}
                    description={t(isMultiSim ? 'duplicateRequestCalloutMultiSim' : 'duplicateRequestCallout')}
                />
            </div>

            <div className='duplicate-sim-request__bottom'>
                <ButtonPrimary role='onPress' onPress={simRequest}>
                    {t('finish')}
                </ButtonPrimary>
            </div>
        </div>
    );
};

export default DuplicateSimRequest;
